import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AlertTriangle, Battery, CheckCircle, Lock, Moon, RotateCcw, Sparkles, Sun } from 'lucide-react';
import { DailyAstroContextDto, DailyAstroProfileScoreDto } from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { getScoreTone } from '../helpers/score-tone';

const PROFILE_KEYS = ['Investment', 'Relationships', 'Health', 'Spiritual', 'Career', 'Travel'];

const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const DashboardLite: React.FC = () => {
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const api = useAstroApiClient();
  const [context, setContext] = useState<DailyAstroContextDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadContext = React.useCallback(async () => {
    if (!auth.user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(false);

    try {
      const date = formatLocalDate(new Date());
      const result = await api.apiAstrologyDailyContextMe(date, PROFILE_KEYS.join(','));
      setContext(result ?? null);
    } catch {
      setContext(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [api, auth.user]);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  const topProfiles = useMemo(
    () =>
      [...(context?.profileScores ?? [])]
        .filter((profile): profile is DailyAstroProfileScoreDto => Boolean(profile.profileKey))
        .sort((left, right) => (right.percentAbsolute ?? 0) - (left.percentAbsolute ?? 0))
        .slice(0, 4),
    [context?.profileScores],
  );

  const retrogradePlanets = context?.retrogradePlanets ?? [];
  const averageScore = context?.averageProfileScore;

  const openProSubscription = () => {
    history.push('/subscription/pro', { returnTo: '/dashboard' });
  };

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6" aria-busy="true" aria-label={t('dashboardLiteLoading')}>
        <div className="space-y-2">
          <div className="h-6 w-48 animate-pulse rounded bg-stone-200" />
          <div className="h-3 w-64 animate-pulse rounded bg-stone-200" />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Card key={index} className="border-stone-200 bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="h-4 w-24 animate-pulse rounded bg-stone-200" />
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-7 w-20 animate-pulse rounded bg-stone-200" />
                <div className="h-3 w-28 animate-pulse rounded bg-stone-100" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[0, 1].map((index) => (
            <Card key={index} className="border-stone-200 bg-white shadow-sm">
              <CardHeader>
                <div className="h-5 w-40 animate-pulse rounded bg-stone-200" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-3 w-full animate-pulse rounded bg-stone-100" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-stone-100" />
                <div className="h-3 w-3/4 animate-pulse rounded bg-stone-100" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card className="border-amber-200 bg-amber-50 shadow-sm">
        <CardContent className="space-y-4 p-6 text-center">
          <p className="text-sm text-amber-800">{t('dashboardLiteLoadError')}</p>
          <Button onClick={loadContext} className="bg-action-700 text-white hover:bg-action-800">
            {t('dashboardLiteRetry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('dashboardLiteTitle')}</h2>
          <p className="mt-1 text-sm text-slate-600">{t('dashboardLiteSubtitle')}</p>
        </div>
        <div className="rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
          <span className="font-medium">{new Date().toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' })}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-sm font-medium text-slate-600">
              <Sun className="mr-2 h-4 w-4 text-amber-500" />
              {t('solarSign')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {context?.sunSign ? t('sign_' + context.sunSign) : '-'}
            </div>
            <p className="mt-1 text-xs text-slate-600">
              {context?.venusSign && context?.marsSign
                ? t('dashboardLiteTransitBlend', {
                    venus: t('sign_' + context.venusSign),
                    mars: t('sign_' + context.marsSign),
                  })
                : t('dashboardLiteCalculatedSignal')}
            </p>
          </CardContent>
        </Card>

        <Card className="border-sky-200 bg-gradient-to-br from-blue-50 to-sky-50 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-sm font-medium text-sky-700">
              <Moon className="mr-2 h-4 w-4 text-sky-600" />
              {t('lunarPhase')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {context?.lunarPhase ? t('phase_' + context.lunarPhase) : '-'}
            </div>
            <p className="mt-1 text-xs text-sky-700">
              {context?.moonSign
                ? t('dashboardPlanetInSign', { planet: t('planet_Moon'), sign: t('sign_' + context.moonSign) })
                : t('dashboardLiteCalculatedSignal')}
            </p>
          </CardContent>
        </Card>

        <Card
          className={`border shadow-sm transition-shadow hover:shadow-md ${
            retrogradePlanets.length > 0
              ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50'
              : 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50'
          }`}
        >
          <CardHeader className="pb-2">
            <CardTitle
              className={`flex items-center text-sm font-medium ${
                retrogradePlanets.length > 0 ? 'text-amber-800' : 'text-emerald-700'
              }`}
            >
              {retrogradePlanets.length > 0 ? (
                <RotateCcw className="mr-2 h-4 w-4 text-amber-600" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4 text-emerald-600" />
              )}
              {t('dashboardRetrogradesTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {retrogradePlanets.length > 0 ? (
              <>
                <div className="mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800">
                    {t('dashboardRetrogradeCount', { count: retrogradePlanets.length })}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {retrogradePlanets.map((planet) => (
                    <span
                      key={planet}
                      className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                    >
                      {t('planet_' + planet)}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-emerald-700">{t('dashboardNone')}</div>
                <p className="mt-1 text-xs text-emerald-600">{t('dashboardDirectEnergyToday')}</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-sm font-medium text-green-700">
              <Battery className="mr-2 h-4 w-4 text-green-600" />
              {t('insightsEnergyTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {typeof averageScore === 'number' ? `${Math.round(averageScore)}%` : '-'}
            </div>
            <p className="mt-1 text-xs text-green-700">{t('insightsEnergyOverall')}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center text-slate-800">
              <Sparkles className="mr-2 h-5 w-5 text-action-500" />
              {t('dashboardLiteTopProfilesTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {topProfiles.length > 0 ? (
              topProfiles.map((profile) => {
                const score = Math.round(profile.percentAbsolute ?? 0);
                return (
                  <div key={profile.profileKey} className="space-y-1.5">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-slate-700">{t('profile_' + profile.profileKey)}</span>
                      <span className="font-semibold text-slate-800">{score}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-stone-100">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${getScoreTone(score)}`}
                        style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                <p className="text-sm font-medium text-slate-700">
                  {context?.hasNatalChart === false ? t('dashboardLiteNoNatalChartTitle') : t('dashboardLiteNoScoresTitle')}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {context?.hasNatalChart === false
                    ? t('dashboardLiteNoNatalChartDescription')
                    : t('dashboardLiteNoScoresDescription')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-action-200 bg-gradient-to-br from-action-50 to-white shadow-sm transition-shadow hover:shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center text-slate-800">
              <Lock className="mr-2 h-5 w-5 text-action-500" />
              {t('dashboardLiteProCardTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-action-100 bg-white/75 p-3">
              <p className="text-sm font-semibold text-slate-800">{t('dashboardLiteProDailyMessageTitle')}</p>
              <p className="mt-1 text-sm text-slate-600">{t('dashboardLiteProDailyMessageDescription')}</p>
            </div>
            <div className="rounded-lg border border-action-100 bg-white/75 p-3">
              <p className="text-sm font-semibold text-slate-800">{t('dashboardLiteProMeditationTitle')}</p>
              <p className="mt-1 text-sm text-slate-600">{t('dashboardLiteProMeditationDescription')}</p>
            </div>
            <Button onClick={openProSubscription} className="w-full bg-action-700 text-white hover:bg-action-800">
              {t('dashboardLiteUpgradeAction')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardLite;
